import React, { useMemo } from 'react';
import Stack from '@mui/material/Stack';
import {
  FleetDashboardFilters,
  FleetDataFilter,
  dashboardFiltersToFleetDataFilters,
} from 'models/fleet-management/FleetDataFilter';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import DealerOverviewWidgets from './widget/DealerOverviewWidgets';
import FleetOverviewWidgets from './widget/FleetOverviewWidgets';
import FleetHealthWidgets from './widget/FleetHealthWidgets';
import FleetPerformanceWidgets from './widget/FleetPerformanceWidgets';
import { FleetDataModel, PageOption } from 'models/enums/fleet-management';
import {
  FleetDataHealth,
  FleetDataMissions,
  FleetDataRobots,
  HEADER_ROBOT_FIELDS,
} from 'models/fleet-management/FleetDataSet';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import useFleetDataset, { FleetDatasetRequest } from 'hooks/useFleetDataset';
import { FLEET_SECTION_GAP } from 'utils/fleet-dashboard-theme';
import { RELIABILITY_CARD_HEIGHT } from './widget/ReliabilitySection';
import {
  CardGridSkeleton,
  MeterListSkeleton,
  SectionSkeleton,
  STATUS_CARD_HEIGHT,
  TableSkeleton,
} from './widget/SectionSkeletons';

const fleetHeaderRequest = (
  model: FleetDataModel,
  filters: FleetDashboardFilters,
  datasetFilters: FleetDataFilter[],
  fields?: string[],
): FleetDatasetRequest => ({
  model,
  fields,
  start: filters.start,
  end: filters.end,
  filters: datasetFilters,
});

/** The weekly output and software version charts' height. */
const OVERVIEW_CHART_HEIGHT = 300;

/** Fleet Overview's shape: summary tiles, reliability cards, the two charts and the table. */
const FleetOverviewSkeleton: React.FC = () => (
  <React.Fragment>
    <SectionSkeleton titleWidth={160}>
      <CardGridSkeleton
        count={5}
        height={STATUS_CARD_HEIGHT}
        item={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}
      />
    </SectionSkeleton>
    <SectionSkeleton titleWidth={220}>
      <CardGridSkeleton count={3} height={RELIABILITY_CARD_HEIGHT} item={{ xs: 12, md: 4 }} />
    </SectionSkeleton>
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={FLEET_SECTION_GAP}>
      <SectionSkeleton sx={{ flex: '1 1 0', minWidth: 0 }}>
        <Skeleton variant="rounded" height={OVERVIEW_CHART_HEIGHT} />
      </SectionSkeleton>
      <SectionSkeleton caption={false} sx={{ flex: '0 1 440px', minWidth: 340 }}>
        <Skeleton variant="rounded" height={OVERVIEW_CHART_HEIGHT} />
      </SectionSkeleton>
    </Stack>
    <SectionSkeleton titleWidth={240}>
      <TableSkeleton />
    </SectionSkeleton>
  </React.Fragment>
);

/** Dealer Overview's shape: the two dealer rankings side by side. */
const DealerOverviewSkeleton: React.FC = () => (
  <Stack gap={1.5}>
    <Skeleton variant="text" width={140} height={22} />
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={FLEET_SECTION_GAP}>
      {[0, 1].map((index) => (
        <SectionSkeleton key={index} sx={{ flex: '1 1 0' }}>
          <MeterListSkeleton rows={8} gap={1} />
        </SectionSkeleton>
      ))}
    </Stack>
  </Stack>
);

const FleetContentSkeleton: React.FC<{ page: PageOption }> = ({ page }) => (
  <Stack gap={FLEET_SECTION_GAP} aria-busy="true" data-testid="fleet-content-skeleton">
    {page === PageOption.DealerOverview ? <DealerOverviewSkeleton /> : <FleetOverviewSkeleton />}
  </Stack>
);

const FleetContent: React.FC<{
  page: PageOption;
  filters: FleetDashboardFilters;
  /** False while a saved dashboard's own filters are still on their way, so nothing fetches twice. */
  filtersReady: boolean;
}> = ({ page, filters, filtersReady }) => {
  const { t } = useTranslation();

  const datasetFilters = useMemo(() => dashboardFiltersToFleetDataFilters(filters), [filters]);

  // The overview pages draw from the same robots, so moving between them with the same filters
  // reads them from the store. Only Fleet Overview shows the weekly missions, and only Fleet
  // Health the weekly health.
  const isOverview = page === PageOption.DealerOverview || page === PageOption.FleetOverview;
  const fetchesRobots = isOverview && filtersReady;
  const needsMissions = page === PageOption.FleetOverview;
  const needsHealth = page === PageOption.FleetHealth;

  const robots = useFleetDataset<FleetDataRobots>(
    fetchesRobots
      ? fleetHeaderRequest(FleetDataModel.ROBOT, filters, datasetFilters, HEADER_ROBOT_FIELDS)
      : null,
  );
  const missions = useFleetDataset<FleetDataMissions>(
    filtersReady && needsMissions
      ? fleetHeaderRequest(FleetDataModel.MISSION, filters, datasetFilters)
      : null,
  );
  const health = useFleetDataset<FleetDataHealth>(
    filtersReady && needsHealth
      ? fleetHeaderRequest(FleetDataModel.ROBOT_HEALTH, filters, datasetFilters)
      : null,
  );

  const overviewDatasets = needsMissions ? [robots, missions] : [robots];
  const isLoading = overviewDatasets.some((d) => d.isLoading);
  const hasError = overviewDatasets.some((d) => d.hasError);

  const robotsData = useMemo(() => robots.data ?? [], [robots.data]);
  const missionsData = useMemo(() => missions.data ?? [], [missions.data]);

  const hasData = overviewDatasets.every((d) => d.data != null);
  const showContent = hasData && !hasError;

  return (
    <Stack gap={FLEET_SECTION_GAP} width="100%">
      {isOverview && (
        <React.Fragment>
          {isLoading && showContent && (
            <LinearProgress
              aria-label={t('loading-dataset')}
              sx={{ height: 2, borderRadius: 1, mb: -1 }}
            />
          )}
          {hasError && <CustomAlert header={t('fleet-management-error')} severity="error" />}
          {isLoading && !showContent && !hasError && <FleetContentSkeleton page={page} />}
          {showContent && (
            <Stack
              gap={FLEET_SECTION_GAP}
              sx={{
                transition: 'opacity 0.2s ease-in-out',
                opacity: isLoading ? 0.5 : 1,
                pointerEvents: isLoading ? 'none' : undefined,
              }}
            >
              {page === PageOption.DealerOverview && (
                <DealerOverviewWidgets robotsData={robotsData} />
              )}

              {page === PageOption.FleetOverview && (
                <FleetOverviewWidgets
                  filters={filters}
                  robotsData={robotsData}
                  missionsData={missionsData}
                />
              )}
            </Stack>
          )}
        </React.Fragment>
      )}

      {/* These pages fetch their own data, so they mount with the header rather than after it
          and every request of the page starts at once. Each section dims and loads on its own. */}
      {filtersReady && page === PageOption.FleetHealth && (
        <React.Fragment>
          {health.hasError && <CustomAlert header={t('fleet-management-error')} severity="error" />}
          <FleetHealthWidgets filters={filters} healthData={health.data} />
        </React.Fragment>
      )}

      {filtersReady && page === PageOption.FleetPerformance && (
        <FleetPerformanceWidgets filters={filters} />
      )}
    </Stack>
  );
};

export default FleetContent;
